import { Box, Tooltip } from '@mui/material'
import Memory from '@mui/icons-material/Memory'
import React from 'react'
import Button from './Button'
import Card from './Card'
import EditBaseDataForm from './EditBaseDataForm'
import OnboardingDocumentsForm from './OnboardingDocumentsForm'
import StripeOnboarding from './StripeOnboarding'
import TextField from './TextField'
import Txt from './Txt'
import setOnboardingStep from '../mutations/setOnboardingStep'
import { handleMutationError } from '../utils/errors'
import { Restaurant } from '../utils/restaurant'
import {
	isAutomaticStep,
	ONBOARDING_SECTIONS,
	PREVIEW_ONBOARDING_SECTIONS,
	useOnboardingState,
} from '../utils/onboardingSteps'

const MARKER_SIZE_REM = '1.75rem'

const sxStep = {
	display: 'flex',
	alignItems: 'flex-start',
	gap: '1rem',
	py: '0.75rem',
	borderTop: '1px solid',
	borderColor: 'divider',
} as const

// Titel, Nummer und Haken stehen in einer Zeile von der Höhe der Nummer — damit sitzt der Titel
// zugeklappt wie aufgeklappt an derselben Stelle.
const sxTitleRow = { minHeight: MARKER_SIZE_REM, display: 'flex', alignItems: 'center' } as const

// Die Nummerierung läuft per CSS-Counter über die Schrittliste.
const sxGuide = {
	display: 'flex',
	flexDirection: 'column',
	gap: '1.5rem',
	counterReset: 'step',
} as const

// Erledigt oder nicht sagt der Haken rechts, die Nummer bleibt farblos.
const sxNumber = {
	'counterIncrement': 'step',
	'&::before': { content: 'counter(step)' },
	'flexShrink': 0,
	'width': MARKER_SIZE_REM,
	'height': MARKER_SIZE_REM,
	'borderRadius': '50%',
	'border': '1px solid',
	'borderColor': 'divider',
	'color': 'text.secondary',
	'display': 'flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'fontWeight': 'bold',
	'fontSize': '0.85rem',
} as const

const sxClickable = { cursor: 'pointer' } as const

// Eine Karte je Abschnitt, mit Überschrift; die Schritte darunter trennen sich durch ihre Oberkante.
const sxSection = { p: '1rem', flexDirection: 'column' } as const
const sxSectionTitle = { mb: '0.25rem' } as const
// Ein Schalter des Abschnitts steht rechts neben der Überschrift.
const sxSectionHeader = { display: 'flex', alignItems: 'center', gap: '1.5rem' } as const

// Aufklappbare Schritte tragen im zugeklappten Zustand eine Spitze nach unten statt eines Kreises —
// die Nummer selbst ist der Aufklapp-Schalter. Der Text wird gegengedreht.
const sxCollapsed = {
	'borderRadius': '50% 50% 50% 0',
	'transform': 'rotate(-45deg)',
	'&::before': { content: 'counter(step)', transform: 'rotate(45deg)' },
} as const

// Firmen-E-Mail und Restaurant-ID nebeneinander — beides braucht man beim Anlegen des Restaurant-Admins.
const sxAdminFields = {
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	gap: '1rem',
	mt: '0.75rem',
} as const

const CHECK_DONE_COLOR = '#2e7d32'

// Kennzeichnet Schritte, deren Haken sich aus einem Stand ergibt statt von Hand gesetzt zu werden.
const sxAutoIcon = { color: 'text.secondary', fontSize: '1.5rem' } as const
// Chip und Haken stehen direkt nebeneinander, der Haken bleibt bündig am rechten Rand.
const sxCheckGroup = {
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
	minHeight: MARKER_SIZE_REM,
} as const

const EMPTY_SET: ReadonlySet<string> = new Set()

const StepMarker = React.memo(
	({
		stepId,
		expandable,
		expanded,
		onToggle,
	}: {
		stepId: string
		expandable: boolean
		expanded: boolean
		onToggle: (stepId: string) => void
	}): JSX.Element => {
		const handleClick = React.useCallback(() => onToggle(stepId), [onToggle, stepId])
		const sx = React.useMemo(
			() => ({
				...sxNumber,
				...(expandable ? sxClickable : null),
				...(expandable && !expanded ? sxCollapsed : null),
			}),
			[expandable, expanded]
		)
		return <Box sx={sx} onClick={expandable ? handleClick : undefined} />
	}
)
StepMarker.displayName = 'StepMarker'

type Props = {
	restaurant?: Restaurant | null
	// Der reduzierte Guide des Preview-Tabs: ohne Vertrag, Zahlung, Unterlagen und Prüfung.
	preview?: boolean
}

const OnboardingGuide = ({ restaurant, preview }: Props): JSX.Element => {
	const [expandedSteps, setExpandedSteps] = React.useState<ReadonlySet<string>>(EMPTY_SET)
	const restaurantId = restaurant?.id ?? null
	const recipientEmail = restaurant?.company?.email ?? null
	const { checkedSteps, isStepDone } = useOnboardingState(restaurant, preview)

	const handleToggleExpand = React.useCallback((step: string) => {
		setExpandedSteps((current) => {
			const next = new Set(current)
			if (!next.delete(step)) next.add(step)
			return next
		})
	}, [])

	const handleToggleStep = React.useCallback(
		async (step: string) => {
			if (!restaurantId) return
			const res = await setOnboardingStep({
				restaurantId,
				step,
				done: !checkedSteps.has(step),
			})
			if (!res.success) {
				handleMutationError('setOnboardingStep', res.errors)
			}
		},
		[restaurantId, checkedSteps]
	)

	return (
		<Box sx={sxGuide}>
			{(preview ? PREVIEW_ONBOARDING_SECTIONS : ONBOARDING_SECTIONS).map((section) => (
				<Card key={section.title} sx={sxSection}>
					<Box sx={sxSectionHeader}>
						<Txt variant="h6" fontWeight="bold" sx={sxSectionTitle}>
							{section.title}
						</Txt>
						{/* Aktive Zahlungsart der Firma: ohne sie oder bei DOOR/DOOR_TEST zahlen Kunden an der Tür. */}
						{section.showPaymentMethod && restaurant ? (
							<Txt variant="body2" color="text.secondary" m="0">
								{restaurant.paymentMethod === 'STRIPE'
									? 'Aktuell: Online per Stripe'
									: restaurant.paymentMethod === 'STRIPE_TEST'
									? 'Aktuell: Online per Stripe (Test)'
									: restaurant.paymentMethod == null ||
									  ['DOOR', 'DOOR_TEST'].includes(restaurant.paymentMethod)
									? 'Aktuell: An der Tür (bar), bis ein Stripe-Konto verbunden ist'
									: `Aktuell: Online (${restaurant.paymentMethod})`}
							</Txt>
						) : null}
					</Box>
					{section.steps.map((step) => {
						const done = isStepDone(step)
						const isAutomatic = isAutomaticStep(step)
						const expanded = expandedSteps.has(step.id)
						return (
							<Box key={step.id} sx={sxStep}>
								<StepMarker
									stepId={step.id}
									expandable={Boolean(
										step.contract ||
											step.stripe ||
											step.companyForm ||
											step.showCompanyEmail
									)}
									expanded={expanded}
									onToggle={handleToggleExpand}
								/>
								<Box flex={1} minWidth={0}>
									<Box sx={sxTitleRow}>
										<Txt variant="body1" fontWeight="bold" m="0">
											{step.title}
										</Txt>
									</Box>
									{step.activeMenu && !done ? (
										<Txt variant="body2" color="text.secondary" m="0.25rem 0 0 0">
											{preview
												? 'Import und Freigabe als Preview laufen in der Menüverwaltung.'
												: 'Import, Prüfung, Freigabe und das Ende einer Preview laufen in der Menüverwaltung.'}
										</Txt>
									) : null}
									{step.showCompanyEmail && expanded ? (
										<Box sx={sxAdminFields}>
											<TextField
												key={recipientEmail}
												label="Restaurant-Admin anlegen mit"
												defaultValue={recipientEmail ?? ''}
												disabled
												variant="outlined"
												width="20rem"
											/>
											<TextField
												key={restaurantId}
												label="Restaurant-ID"
												defaultValue={restaurantId ?? ''}
												disabled
												variant="outlined"
												width="10rem"
											/>
										</Box>
									) : null}
									{step.showCompanyEmail && !recipientEmail ? (
										<Txt variant="body2" color="error.main" m="0.25rem 0 0 0">
											Für die Firma ist keine E-Mail-Adresse hinterlegt.
										</Txt>
									) : null}
									{step.contract && expanded ? (
										<Box mt="1rem">
											<OnboardingDocumentsForm restaurant={restaurant} />
										</Box>
									) : null}
									{step.companyForm && expanded ? (
										<Box mt="1rem">
											<EditBaseDataForm restaurant={restaurant} />
										</Box>
									) : null}
									{step.stripe && expanded ? (
										<Box mt="1rem">
											<StripeOnboarding restaurant={restaurant} />
										</Box>
									) : null}
								</Box>
								<Box sx={sxCheckGroup}>
									{isAutomatic ? (
										<Tooltip title="Wird automatisch bestimmt" arrow>
											<Memory sx={sxAutoIcon} />
										</Tooltip>
									) : null}
									{step.contract ? null : (
										<Button
											id={step.id}
											onClick={handleToggleStep}
											disabled={!restaurantId || isAutomatic}
											iconName={done ? 'CheckCircle' : 'CheckCircleOutline'}
											iconColor={done ? CHECK_DONE_COLOR : undefined}
										/>
									)}
								</Box>
							</Box>
						)
					})}
				</Card>
			))}
		</Box>
	)
}

export default React.memo(OnboardingGuide)
